/**
 * Provider catalog — single source of truth for the apps we surface in the
 * Connect lane, the My Business "Connected Apps" panel, and the research-page
 * connection proposals. Categories + Composio toolkit slugs + brand visuals.
 *
 * The slugs here MUST match Composio's toolkit registry. When Composio adds
 * or renames a toolkit, update this file — it's the only place we need to
 * change. (validx has its own slug-normalization layer that maps common
 * mistakes like `google-drive` → `google_drive` to these canonical forms.)
 *
 * ⚠️  KEEP IN SYNC with backend/app/services/provider_catalog.py — it
 * enforces the connector-restricted company tool stack from the same list.
 */

export interface ProviderInfo {
  /** Composio toolkit slug — what we pass to authorizeApp. */
  slug: string;
  /** Display name. */
  label: string;
  /** Brand background color for the logo chip. */
  color: string;
  /** Single-letter or short initial shown in the logo chip. */
  initial: string;
  /** Override the white-on-color text color (rare — Slack's purple needs it). */
  textColor?: string;
}

export interface CategoryInfo {
  id: string;
  label: string;
  /** Lucide icon name — resolved at render time via a map. */
  iconKey: 'mail' | 'calendar' | 'folder' | 'code' | 'chat' | 'crm' | 'analytics' | 'payments' | 'tasks' | 'design';
  providers: ProviderInfo[];
}

export const PROVIDER_CATEGORIES: CategoryInfo[] = [
  {
    id: 'email',
    label: 'Email',
    iconKey: 'mail',
    providers: [
      { slug: 'gmail',   label: 'Gmail',   color: '#EA4335', initial: 'G' },
      { slug: 'outlook', label: 'Outlook', color: '#0078D4', initial: 'O' },
    ],
  },
  {
    id: 'calendar',
    label: 'Calendar',
    iconKey: 'calendar',
    providers: [
      { slug: 'google_calendar',  label: 'Google Calendar',  color: '#4285F4', initial: 'G' },
      { slug: 'outlook_calendar', label: 'Outlook Calendar', color: '#28A8EA', initial: 'O' },
    ],
  },
  {
    id: 'files',
    label: 'Files',
    iconKey: 'folder',
    providers: [
      { slug: 'google_drive', label: 'Google Drive',    color: '#1FA463', initial: 'G' },
      { slug: 'dropbox',      label: 'Dropbox',  color: '#0061FF', initial: 'D' },
      { slug: 'onedrive',     label: 'OneDrive', color: '#0061BF', initial: 'O' },
    ],
  },
  {
    id: 'code',
    label: 'Code',
    iconKey: 'code',
    providers: [
      { slug: 'github',    label: 'GitHub',    color: '#181717', initial: 'G' },
      { slug: 'gitlab',    label: 'GitLab',    color: '#FC6D26', initial: 'G' },
    ],
  },
  {
    id: 'chat',
    label: 'Team chat',
    iconKey: 'chat',
    providers: [
      { slug: 'slack',   label: 'Slack',   color: '#611F69', initial: 'S' },
      { slug: 'discord', label: 'Discord', color: '#5865F2', initial: 'D' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    iconKey: 'crm',
    providers: [
      { slug: 'hubspot',    label: 'HubSpot',    color: '#FF7A59', initial: 'H' },
      { slug: 'salesforce', label: 'Salesforce', color: '#00A1E0', initial: 'S' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    iconKey: 'analytics',
    providers: [
      { slug: 'google_analytics', label: 'Google Analytics', color: '#F9AB00', initial: 'G', textColor: '#111827' },
      { slug: 'mixpanel',         label: 'Mixpanel',         color: '#7856FF', initial: 'M' },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    iconKey: 'payments',
    providers: [
      { slug: 'stripe',  label: 'Stripe',  color: '#635BFF', initial: 'S' },
      { slug: 'shopify', label: 'Shopify', color: '#7AB55C', initial: 'S' },
    ],
  },
  {
    id: 'tasks',
    label: 'Project work',
    iconKey: 'tasks',
    providers: [
      { slug: 'notion', label: 'Notion', color: '#000000', initial: 'N' },
      { slug: 'linear', label: 'Linear', color: '#5E6AD2', initial: 'L' },
      { slug: 'jira',   label: 'Jira',   color: '#0052CC', initial: 'J' },
      { slug: 'asana',  label: 'Asana',  color: '#F06A6A', initial: 'A' },
      { slug: 'trello', label: 'Trello', color: '#0079BF', initial: 'T' },
    ],
  },
  {
    id: 'design',
    label: 'Design',
    iconKey: 'design',
    providers: [
      { slug: 'figma', label: 'Figma', color: '#F24E1E', initial: 'F' },
    ],
  },
];

/** Flat slug → { provider, category } lookup. Built once at module load. */
const PROVIDER_INDEX: Record<string, { provider: ProviderInfo; category: CategoryInfo }> = {};
for (const cat of PROVIDER_CATEGORIES) {
  for (const p of cat.providers) PROVIDER_INDEX[p.slug] = { provider: p, category: cat };
}

/** Look up a provider by slug. Returns null if we don't know about it. */
export function lookupProvider(slug: string): { provider: ProviderInfo; category: CategoryInfo } | null {
  if (!slug) return null;
  const direct = PROVIDER_INDEX[slug];
  if (direct) return direct;
  // Tolerate hyphen / case variants the agent might pass.
  const norm = slug.toLowerCase().replace(/-/g, '_');
  return PROVIDER_INDEX[norm] || null;
}

/** Synthesize a provider entry for an unknown slug (deterministic color). */
const FALLBACK_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6'];
export function synthesizeProvider(slug: string): ProviderInfo {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  const color = FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
  const label = slug
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
  return {
    slug,
    label,
    color,
    initial: slug.charAt(0).toUpperCase() || '?',
  };
}

/** Resolve a provider for any slug — returns the catalog entry or a synthesized one. */
export function resolveProvider(slug: string): { provider: ProviderInfo; category: CategoryInfo | null } {
  const found = lookupProvider(slug);
  if (found) return found;
  return { provider: synthesizeProvider(slug), category: null };
}
